import { BySalesforceResourceMap } from 'module/api/integrations/salesforce/types';
import { Modal } from 'module/ui/components/atoms/modal';
import { Select, CustomOption } from 'module/ui/components/atoms/select';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

type ResourceTypeOptions = {
  projects: CustomOption[];
  accounts: CustomOption[];
  users: CustomOption[];
};

type AddResourceModalTypes = {
  resources: BySalesforceResourceMap;
  visibility: boolean;
  resourceTypeOptions: ResourceTypeOptions;
  resourceTypeStatus: 'error' | 'pending' | 'success';
  creationStatus: 'error' | 'pending' | 'success' | 'idle';
  selectedNativeResourceType: 'project' | 'company' | 'user';
  handleClose: () => void;
  handleSubmit: (selected?: string) => void;
};

export const AddResourceModal = ({
  resources,
  resourceTypeOptions,
  creationStatus,
  resourceTypeStatus,
  visibility,
  handleSubmit,
  handleClose,
  selectedNativeResourceType,
}: AddResourceModalTypes) => {
  const [selected, setSelected] = useState<
    { label: string; value: string } | undefined
  >(undefined);

  const selectedResources = useMemo(
    () => Object.values(resources)?.map((value) => value.externalResourceType),
    [resources],
  );

  // Get the appropriate options based on selected native resource type
  const currentOptions = useMemo(() => {
    const optionsMap = {
      project: resourceTypeOptions.projects,
      company: resourceTypeOptions.accounts,
      user: resourceTypeOptions.users,
    };

    return optionsMap[selectedNativeResourceType] || [];
  }, [resourceTypeOptions, selectedNativeResourceType]);

  const filteredOptions = useMemo(() => {
    return currentOptions?.filter(
      (resourceType) =>
        !selectedResources.includes(resourceType.value.toString()),
    );
  }, [currentOptions, selectedResources]);

  const handleSelection = () => {
    handleSubmit(selected?.value);
  };

  return (
    <Modal
      onOk={handleSelection}
      onCancel={handleClose}
      visible={visibility}
      title={
        <FormattedMessage id="settings.integrations.sf.automation.create.object.modal.title" />
      }
      okButtonProps={{ disabled: resourceTypeStatus === 'pending' }}
      destroyOnClose
    >
      <Select
        autoFocus
        options={filteredOptions}
        isLoading={resourceTypeStatus === 'pending'}
        isDisabled={creationStatus === 'pending'}
        onChange={setSelected}
        value={selected}
        isClearable
      />
    </Modal>
  );
};
